import React, { useState, useId } from 'react';
import { Chip, Box, Typography, Stack, Popover, useTheme, styled, CircularProgress } from '@mui/material';
import { ScoringStatus } from '../types';
import { getScoreColors, getScoreLabel } from './score.utils';
import { sanitizeHtml } from '@helper/string.utils';

interface ScoringScoreBadgeProps {
    score?: number | null;
    status: ScoringStatus;
    scoreExplainer?: string;
    onClick?: () => void;
    onViewMoreClick?: () => void;
}

const getStatusLabel = (status: ScoringStatus): string => {
    switch (status) {
        case 'pending': return 'En attente';
        case 'in_progress': return 'En cours';
        case 'error': return 'Erreur';
        default: return '—';
    }
};

const ScoreIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M9.45215 6.25V8.75C9.45215 9.1645 9.11665 9.5 8.70215 9.5C8.28765 9.5 7.95215 9.1645 7.95215 8.75V6.25C7.95215 5.8355 8.28765 5.5 8.70215 5.5C9.11665 5.5 9.45215 5.8355 9.45215 6.25ZM11.2021 5C10.7876 5 10.4521 5.3355 10.4521 5.75V9.25C10.4521 9.939 9.89115 10.5 9.20215 10.5H2.70215C2.01315 10.5 1.45215 9.939 1.45215 9.25V2.75C1.45215 2.061 2.01315 1.5 2.70215 1.5H6.20215C6.61665 1.5 6.95215 1.1645 6.95215 0.75C6.95215 0.3355 6.61615 0 6.20215 0H2.70215C1.18565 0 -0.0478516 1.2335 -0.0478516 2.75V9.25C-0.0478516 10.7665 1.18565 12 2.70215 12H9.20215C10.7186 12 11.9521 10.7665 11.9521 9.25V5.75C11.9521 5.3355 11.6166 5 11.2021 5ZM6.15315 3.4135L7.43015 8.5695C7.52965 8.9715 7.28415 9.378 6.88215 9.4775C6.82165 9.4925 6.76115 9.4995 6.70165 9.4995C6.36465 9.4995 6.05865 9.271 5.97415 8.9295L5.86765 8.4995H4.03665L3.93015 8.9295C3.83065 9.3315 3.42515 9.5765 3.02215 9.4775C2.62015 9.378 2.37465 8.9715 2.47415 8.5695L3.74215 3.446C3.90965 2.8765 4.39265 2.516 4.95215 2.516C5.51165 2.516 5.99415 2.877 6.15315 3.4135ZM5.71515 7L5.02765 4.223C5.00815 4.1435 4.89465 4.1435 4.87515 4.223L4.18815 7H5.71465H5.71515ZM7.79615 2.7425L8.78515 3.159L9.17965 4.146C9.26515 4.3595 9.47165 4.5 9.70215 4.5C9.93265 4.5 10.1391 4.36 10.2246 4.146L10.6171 3.1645L11.5986 2.772C11.8121 2.6865 11.9526 2.48 11.9526 2.2495C11.9526 2.019 11.8126 1.8125 11.5986 1.727L10.6171 1.3345L10.2246 0.353C10.1391 0.14 9.93215 0 9.70215 0C9.47215 0 9.26515 0.14 9.17965 0.354L8.78965 1.3295L7.81565 1.6985C7.60065 1.78 7.45665 1.984 7.45215 2.214C7.44815 2.444 7.58415 2.6535 7.79615 2.7425Z"
            fill="white"
        />
    </svg>
);

export const AIIcon = ({
    size = 18
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_ssb_ai)">
            <path d="M18.904 12.5V17.5C18.904 18.329 18.233 19 17.404 19C16.575 19 15.904 18.329 15.904 17.5V12.5C15.904 11.671 16.575 11 17.404 11C18.233 11 18.904 11.671 18.904 12.5ZM22.404 10C21.575 10 20.904 10.671 20.904 11.5V18.5C20.904 19.878 19.782 21 18.404 21H5.40399C4.02599 21 2.90399 19.878 2.90399 18.5V5.5C2.90399 4.122 4.02599 3 5.40399 3H12.404C13.233 3 13.904 2.329 13.904 1.5C13.904 0.671 13.232 0 12.404 0H5.40399C2.37099 0 -0.0960083 2.467 -0.0960083 5.5V18.5C-0.0960083 21.533 2.37099 24 5.40399 24H18.404C21.437 24 23.904 21.533 23.904 18.5V11.5C23.904 10.671 23.233 10 22.404 10ZM12.306 6.827L14.86 17.139C15.059 17.943 14.568 18.756 13.764 18.955C13.643 18.985 13.522 18.999 13.403 18.999C12.729 18.999 12.117 18.542 11.948 17.859L11.735 16.999H8.07299L7.85999 17.859C7.66099 18.663 6.84999 19.153 6.04399 18.955C5.23999 18.756 4.74899 17.943 4.94799 17.139L7.48399 6.892C7.81899 5.753 8.78499 5.032 9.90399 5.032C11.023 5.032 11.988 5.754 12.306 6.827ZM11.43 14L10.055 8.446C10.016 8.287 9.78899 8.287 9.74999 8.446L8.37599 14H11.429H11.43ZM15.592 5.485L17.57 6.318L18.359 8.292C18.53 8.719 18.943 9 19.404 9C19.865 9 20.278 8.72 20.449 8.292L21.234 6.329L23.197 5.544C23.624 5.373 23.905 4.96 23.905 4.499C23.905 4.038 23.625 3.625 23.197 3.454L21.234 2.669L20.449 0.706C20.278 0.28 19.864 0 19.404 0C18.944 0 18.53 0.28 18.359 0.708L17.579 2.659L15.631 3.397C15.201 3.56 14.913 3.968 14.904 4.428C14.896 4.888 15.168 5.307 15.592 5.485Z" fill="currentColor" />
        </g>
        <defs>
            <clipPath id="clip0_ssb_ai">
                <rect width="24" height="24" fill="currentColor" />
            </clipPath>
        </defs>
    </svg>
);

const Background = ({ color1, color2 }: { color1: string; color2: string }) => {
    const gradientId1 = useId();
    const gradientId2 = useId();
    return (
        <svg width="76" height="24" viewBox="0 0 76 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="22.8302" cy="22.8302" r="22.4497" transform="matrix(-1 0 0 1 14.4837 -0.478027)" stroke={`url(#${gradientId1})`} strokeWidth="0.761006" />
            <circle cx="35.3868" cy="35.3868" r="35.0063" transform="matrix(-1 0 0 1 35.0309 -2)" stroke={`url(#${gradientId2})`} strokeWidth="0.761006" />
            <path d="M8.05992 7.48629C8.04117 7.49984 8.02716 7.51895 8.01988 7.54092L7.8236 8.13029C7.78042 8.25987 7.70767 8.37761 7.61109 8.47419C7.51451 8.57077 7.39677 8.64352 7.26719 8.68669L6.67782 8.88298C6.65586 8.89026 6.63674 8.90427 6.6232 8.92302C6.60965 8.94178 6.60236 8.96433 6.60236 8.98747C6.60236 9.0106 6.60965 9.03315 6.6232 9.05191C6.63674 9.07067 6.65586 9.08468 6.67782 9.09195L7.26719 9.28824C7.39677 9.33141 7.51451 9.40417 7.61109 9.50074C7.70767 9.59732 7.78042 9.71506 7.8236 9.84464L8.01988 10.434C8.02716 10.456 8.04117 10.4751 8.05992 10.4886C8.07868 10.5022 8.10123 10.5095 8.12437 10.5095C8.14751 10.5095 8.17006 10.5022 8.18881 10.4886C8.20757 10.4751 8.22158 10.456 8.22885 10.434L8.42514 9.84464C8.46831 9.71506 8.54107 9.59732 8.63765 9.50074C8.73422 9.40417 8.85197 9.33141 8.98154 9.28824L9.57092 9.09195C9.59288 9.08468 9.61199 9.07067 9.62554 9.05191C9.63909 9.03315 9.64638 9.0106 9.64638 8.98747C9.64638 8.96433 9.63909 8.94178 9.62554 8.92302C9.61199 8.90427 9.59288 8.89026 9.57092 8.88298L8.98154 8.68669C8.71881 8.59895 8.51289 8.39302 8.42514 8.13029L8.22885 7.54092C8.22158 7.51895 8.20757 7.49984 8.18881 7.48629C8.17006 7.47275 8.14751 7.46545 8.12437 7.46545C8.10123 7.46545 8.07868 7.47275 8.05992 7.48629Z" fill={color1} />
            <path d="M21.758 20.4233C21.7393 20.4369 21.7253 20.456 21.718 20.4779L21.5217 21.0673C21.4785 21.1969 21.4058 21.3146 21.3092 21.4112C21.2126 21.5078 21.0949 21.5805 20.9653 21.6237L20.3759 21.82C20.354 21.8273 20.3349 21.8413 20.3213 21.86C20.3078 21.8788 20.3005 21.9013 20.3005 21.9245C20.3005 21.9476 20.3078 21.9702 20.3213 21.9889C20.3349 22.0077 20.354 22.0217 20.3759 22.029L20.9653 22.2253C21.0949 22.2684 21.2126 22.3412 21.3092 22.4378C21.4058 22.5343 21.4785 22.6521 21.5217 22.7817L21.718 23.371C21.7253 23.393 21.7393 23.4121 21.758 23.4257C21.7768 23.4392 21.7994 23.4465 21.8225 23.4465C21.8456 23.4465 21.8682 23.4392 21.8869 23.4257C21.9057 23.4121 21.9197 23.393 21.927 23.371L22.1233 22.7817C22.1664 22.6521 22.2392 22.5343 22.3358 22.4378C22.4323 22.3412 22.5501 22.2684 22.6797 22.2253L23.269 22.029C23.291 22.0217 23.3101 22.0077 23.3237 21.9889C23.3372 21.9702 23.3445 21.9476 23.3445 21.9245C23.3445 21.9013 23.3372 21.8788 23.3237 21.86C23.3101 21.8413 23.291 21.8273 23.269 21.82L22.6797 21.6237C22.4169 21.536 22.211 21.33 22.1233 21.0673L21.927 20.4779C21.9197 20.456 21.9057 20.4369 21.8869 20.4233C21.8682 20.4098 21.8456 20.4025 21.8225 20.4025C21.7994 20.4025 21.7768 20.4098 21.758 20.4233Z" fill={color1} />
            <path d="M10.3429 16.6184C10.3242 16.6319 10.3102 16.651 10.3029 16.673L10.1066 17.2624C10.0634 17.3919 9.99069 17.5097 9.89411 17.6063C9.79753 17.7028 9.67979 17.7756 9.55021 17.8188L8.96084 18.0151C8.93888 18.0223 8.91976 18.0363 8.90622 18.0551C8.89267 18.0739 8.88538 18.0964 8.88538 18.1195C8.88538 18.1427 8.89267 18.1652 8.90622 18.184C8.91976 18.2027 8.93888 18.2168 8.96084 18.224L9.55021 18.4203C9.67979 18.4635 9.79753 18.5362 9.89411 18.6328C9.99069 18.7294 10.0634 18.8471 10.1066 18.9767L10.3029 19.5661C10.3102 19.5881 10.3242 19.6072 10.3429 19.6207C10.3617 19.6343 10.3843 19.6416 10.4074 19.6416C10.4305 19.6416 10.4531 19.6343 10.4718 19.6207C10.4906 19.6072 10.5046 19.5881 10.5119 19.5661L10.7082 18.9767C10.7513 18.8471 10.8241 18.7294 10.9207 18.6328C11.0172 18.5362 11.135 18.4635 11.2646 18.4203L11.8539 18.224C11.8759 18.2168 11.895 18.2027 11.9086 18.184C11.9221 18.1652 11.9294 18.1427 11.9294 18.1195C11.9294 18.0964 11.9221 18.0739 11.9086 18.0551C11.895 18.0363 11.8759 18.0223 11.8539 18.0151L11.2646 17.8188C11.0018 17.731 10.7959 17.5251 10.7082 17.2624L10.5119 16.673C10.5046 16.651 10.4906 16.6319 10.4718 16.6184C10.4531 16.6048 10.4305 16.5975 10.4074 16.5975C10.3843 16.5975 10.3617 16.6048 10.3429 16.6184Z" fill={color1} />
            <path d="M18.714 9.76925C18.6953 9.7828 18.6813 9.80191 18.674 9.82388L18.4777 10.4133C18.4345 10.5428 18.3618 10.6606 18.2652 10.7571C18.1686 10.8537 18.0509 10.9265 17.9213 10.9697L17.3319 11.1659C17.31 11.1732 17.2909 11.1872 17.2773 11.206C17.2638 11.2247 17.2565 11.2473 17.2565 11.2704C17.2565 11.2936 17.2638 11.3161 17.2773 11.3349C17.2909 11.3536 17.31 11.3676 17.3319 11.3749L17.9213 11.5712C18.0509 11.6144 18.1686 11.6871 18.2652 11.7837C18.3618 11.8803 18.4345 11.998 18.4777 12.1276L18.674 12.717C18.6813 12.7389 18.6953 12.7581 18.714 12.7716C18.7328 12.7851 18.7553 12.7924 18.7785 12.7924C18.8016 12.7924 18.8242 12.7851 18.8429 12.7716C18.8617 12.7581 18.8757 12.7389 18.883 12.717L19.0793 12.1276C19.1224 11.998 19.1952 11.8803 19.2918 11.7837C19.3883 11.6871 19.5061 11.6144 19.6357 11.5712L20.225 11.3749C20.247 11.3676 20.2661 11.3536 20.2797 11.3349C20.2932 11.3161 20.3005 11.2936 20.3005 11.2704C20.3005 11.2473 20.2932 11.2247 20.2797 11.206C20.2661 11.1872 20.247 11.1732 20.225 11.1659L19.6357 10.9697C19.3729 10.8819 19.167 10.676 19.0793 10.4133L18.883 9.82388C18.8757 9.80191 18.8617 9.7828 18.8429 9.76925C18.8242 9.7557 18.8016 9.74841 18.7785 9.74841C18.7553 9.74841 18.7328 9.7557 18.714 9.76925Z" fill={color1} />
            <path d="M0.449878 14.3353C0.431121 14.3488 0.417111 14.368 0.409838 14.3899L0.21355 14.9793C0.170378 15.1089 0.0976212 15.2266 0.00104415 15.3232C-0.0955328 15.4198 -0.213275 15.4925 -0.342853 15.5357L-0.932225 15.732C-0.95419 15.7393 -0.973304 15.7533 -0.986851 15.772C-1.0004 15.7908 -1.00769 15.8133 -1.00769 15.8365C-1.00769 15.8596 -1.0004 15.8822 -0.986851 15.9009C-0.973304 15.9197 -0.95419 15.9337 -0.932225 15.9409L-0.342853 16.1372C-0.213275 16.1804 -0.0955328 16.2532 0.00104415 16.3497C0.0976212 16.4463 0.170378 16.5641 0.21355 16.6936L0.409838 17.283C0.417111 17.305 0.431121 17.3241 0.449878 17.3376C0.468635 17.3512 0.491184 17.3585 0.514322 17.3585C0.53746 17.3585 0.560009 17.3512 0.578766 17.3376C0.597522 17.3241 0.611533 17.305 0.618806 17.283L0.815094 16.6936C0.858266 16.5641 0.931023 16.4463 1.0276 16.3497C1.12418 16.2532 1.24192 16.1804 1.3715 16.1372L1.96087 15.9409C1.98283 15.9337 2.00195 15.9197 2.0155 15.9009C2.02904 15.8822 2.03633 15.8596 2.03633 15.8365C2.03633 15.8133 2.02904 15.7908 2.0155 15.772C2.00195 15.7533 1.98283 15.7393 1.96087 15.732L1.3715 15.5357C1.10877 15.4479 0.90284 15.242 0.815094 14.9793L0.618806 14.3899C0.611533 14.368 0.597522 14.3488 0.578766 14.3353C0.560009 14.3217 0.53746 14.3145 0.514322 14.3145C0.491184 14.3145 0.468635 14.3217 0.449878 14.3353Z" fill={color2} />
            <path d="M3.49392 22.7064C3.47516 22.7199 3.46115 22.739 3.45387 22.761L3.25759 23.3504C3.21442 23.48 3.14166 23.5977 3.04508 23.6943C2.9485 23.7909 2.83076 23.8636 2.70118 23.9068L2.11181 24.1031C2.08985 24.1103 2.07073 24.1244 2.05719 24.1431C2.04364 24.1619 2.03635 24.1844 2.03635 24.2076C2.03635 24.2307 2.04364 24.2532 2.05719 24.272C2.07073 24.2908 2.08985 24.3048 2.11181 24.312L2.70118 24.5083C2.83076 24.5515 2.9485 24.6243 3.04508 24.7208C3.14166 24.8174 3.21442 24.9352 3.25759 25.0647L3.45387 25.6541C3.46115 25.6761 3.47516 25.6952 3.49392 25.7087C3.51267 25.7223 3.53522 25.7296 3.55836 25.7296C3.5815 25.7296 3.60405 25.7223 3.6228 25.7087C3.64156 25.6952 3.65557 25.6761 3.66284 25.6541L3.85913 25.0647C3.9023 24.9352 3.97506 24.8174 4.07164 24.7208C4.16821 24.6243 4.28596 24.5515 4.41553 24.5083L5.00491 24.312C5.02687 24.3048 5.04598 24.2908 5.05953 24.272C5.07308 24.2532 5.08037 24.2307 5.08037 24.2076C5.08037 24.1844 5.07308 24.1619 5.05953 24.1431C5.04598 24.1244 5.02687 24.1103 5.00491 24.1031L4.41553 23.9068C4.1528 23.819 3.94688 23.6131 3.85913 23.3504L3.66284 22.761C3.65557 22.739 3.64156 22.7199 3.6228 22.7064C3.60405 22.6928 3.5815 22.6855 3.55836 22.6855C3.53522 22.6855 3.51267 22.6928 3.49392 22.7064Z" fill={color2} />
            <defs>
                <linearGradient id={gradientId1} x1="22.8302" y1="45.6604" x2="5.44314e-07" y2="27.3962" gradientUnits="userSpaceOnUse">
                    <stop stopColor={color1} />
                    <stop offset="1" stopColor={color2} />
                </linearGradient>
                <linearGradient id={gradientId2} x1="51.3679" y1="56.695" x2="3.04402" y2="57.0755" gradientUnits="userSpaceOnUse">
                    <stop stopColor={color1} />
                    <stop offset="1" stopColor={color2} />
                </linearGradient>
            </defs>
        </svg>
    );
};

const ScoreContainer = styled('div')<{ borderColor: string; background: string }>(({ borderColor, background }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 6px',
    borderRadius: '8px',
    background: background,
    border: `1px solid ${borderColor}`,
    color: 'white',
    fontSize: '13px',
    fontWeight: 700,
    fontFamily: 'Plus Jakarta Sans',
    lineHeight: '16px',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    width: 'fit-content',
}));

const PopoverScoreContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    borderRadius: '8px',
    backgroundColor: theme.palette.background.default,
    width: '259px',
}));

const CornerBorders = styled(Box)<{ borderColor: string }>(({ borderColor }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '1px',
        left: '1px',
        width: '12px',
        height: '12px',
        borderTop: `1px solid ${borderColor}`,
        borderLeft: `1px solid ${borderColor}`,
        borderTopLeftRadius: '6px',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: '1px',
        right: '1px',
        width: '12px',
        height: '12px',
        borderTop: `1px solid ${borderColor}`,
        borderRight: `1px solid ${borderColor}`,
        borderTopRightRadius: '6px',
    },
}));

const BottomCornerBorders = styled(Box)<{ borderColor: string }>(({ borderColor }) => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    pointerEvents: 'none',
    '&::before': {
        content: '""',
        position: 'absolute',
        bottom: '1px',
        left: '1px',
        width: '12px',
        height: '12px',
        borderBottom: `1px solid ${borderColor}`,
        borderLeft: `1px solid ${borderColor}`,
        borderBottomLeftRadius: '6px',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '1px',
        right: '1px',
        width: '12px',
        height: '12px',
        borderBottom: `1px solid ${borderColor}`,
        borderRight: `1px solid ${borderColor}`,
        borderBottomRightRadius: '6px',
    },
}));

const PopoverScoreDisplay = styled(Box)<{ background: string }>(({ background }) => ({
    borderRadius: '10px',
    width: '58px',
    height: '46px',
    position: 'relative',
    gap: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: background,
    overflow: 'hidden',
}));

const ScoreNumber = styled(Typography)<{ color: string }>(({ color }) => ({
    fontFamily: 'Lemon milk pro, Plus Jakarta Sans',
    fontSize: '24px',
    fontWeight: 400,
    color: color,
    position: 'relative',
    zIndex: 2,
}));

const PercentSymbol = styled(Typography)<{ color: string }>(({ color }) => ({
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: '16.356px',
    color: color,
    zIndex: 2,
    marginTop: '6px',
}));

export const ScoringScoreBadge = ({ score, status, scoreExplainer, onClick, onViewMoreClick }: ScoringScoreBadgeProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    if (status === 'done' && score !== null && score !== undefined) {
        const { color, borderColor, background, softBackground } = getScoreColors(score);
        const scoreLabel = getScoreLabel(score);

        return (
            <>
                <ScoreContainer
                    borderColor={borderColor}
                    background={background}
                    aria-owns={open ? 'score-badge-popover' : undefined}
                    aria-haspopup="true"
                    onClick={(e) => { handlePopoverOpen(e); onClick?.(); }}
                >
                    <Box sx={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', zIndex: 10 }}>
                        <Background color1={color} color2={borderColor} />
                    </Box>
                    <Stack direction="row" alignItems="center" sx={{ position: 'relative', zIndex: 11, gap: 0.5 }}>
                        <ScoreIcon />
                        <span>{Math.round(score)}%</span>
                        <i className="fi fi-br-angle-small-right" />
                    </Stack>
                </ScoreContainer>

                <Popover
                    id="score-badge-popover"
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    PaperProps={{
                        sx: {
                            p: '16px 12px',
                            maxWidth: '283px',
                            boxShadow: '0px 4px 80px 0px rgba(112, 144, 176, 0.14)',
                            borderRadius: '12px',
                        },
                    }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Stack gap={2} sx={{ maxWidth: '283px' }}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <AIIcon />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontFamily: 'Plus Jakarta Sans',
                                    fontSize: '20px',
                                    fontWeight: 600,
                                    lineHeight: '24px',
                                }}
                            >
                                Score IA
                            </Typography>
                        </Stack>

                        <PopoverScoreContainer>
                            <Stack sx={{ flex: 1, gap: '0.761px' }}>
                                <Typography
                                    sx={{
                                        fontFamily: 'Plus Jakarta Sans',
                                        fontSize: '15.22px',
                                        fontWeight: 600,
                                        lineHeight: '18.264px',
                                    }}
                                >
                                    Compatibilité IA
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: 'Plus Jakarta Sans',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        lineHeight: '16px',
                                    }}
                                >
                                    {scoreLabel}
                                </Typography>
                            </Stack>
                            <PopoverScoreDisplay background={softBackground}>
                                <CornerBorders borderColor={borderColor} />
                                <BottomCornerBorders borderColor={borderColor} />
                                <Stack sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: 'row' }}>
                                    <ScoreNumber color={color}>{Math.round(score)}</ScoreNumber>
                                    <PercentSymbol color={color}>%</PercentSymbol>
                                </Stack>
                            </PopoverScoreDisplay>
                        </PopoverScoreContainer>

                        {scoreExplainer && (
                            <Stack gap={0.5} sx={{ position: 'relative' }}>
                                <Typography
                                    sx={{
                                        fontFamily: 'Plus Jakarta Sans',
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        lineHeight: '16px',
                                    }}
                                >
                                    Description du Score :
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: 'Plus Jakarta Sans',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        lineHeight: '16px',
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 3,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        height: '49px',
                                    }}
                                >
                                    {sanitizeHtml(scoreExplainer)}
                                </Typography>
                                {onViewMoreClick && (
                                    <Typography
                                        onClick={onViewMoreClick}
                                        sx={{
                                            fontFamily: 'Plus Jakarta Sans',
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            lineHeight: '16px',
                                            color: theme.palette.primary.main,
                                            textAlign: 'right',
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' },
                                        }}
                                    >
                                        ... voir plus
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Popover>
            </>
        );
    }

    const statusChipSx = (() => {
        switch (status) {
            case 'error':
                return {
                    backgroundColor: `${theme.palette.errorRed.main}18`,
                    color: theme.palette.errorRed.main,
                    border: `1px solid ${theme.palette.errorRed.main}40`,
                };
            case 'in_progress':
                return {
                    backgroundColor: `${theme.palette.successGreen.main}18`,
                    color: theme.palette.successGreen.main,
                    border: `1px solid ${theme.palette.successGreen.main}40`,
                };
            case 'pending':
                return {
                    backgroundColor: `${theme.palette.warningOrange.main}18`,
                    color: theme.palette.warningOrange.main,
                    border: `1px solid ${theme.palette.warningOrange.main}40`,
                };
            default:
                return {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.text.secondary,
                };
        }
    })();

    return (
        <Chip
            label={getStatusLabel(status)}
            size="small"
            icon={
                status === 'in_progress' ? (
                    <CircularProgress
                        size={10}
                        thickness={6}
                        sx={{ color: theme.palette.successGreen.main, ml: '6px' }}
                    />
                ) : undefined
            }
            sx={{
                borderRadius: '6px',
                fontStyle: 'italic',
                ...statusChipSx,
            }}
        />
    );
};

export default ScoringScoreBadge;
